/**
 * epgTimeIndicator.tsx
 */
import EpgTimeIndicatorBody from '@components/atoms/container/epgTimeIndicatorBody';
import EpgTimeIndicatorWrapper from '@components/atoms/container/epgTimeIndicatorWrapper';
import EpgTimeIndicatorHeader from '@components/atoms/icons/epgTimeIndicatorHeader';
import MainContext from '@shared/components/utils/context/mainContext';
import { selectLayout } from '@shared/store/slices/main.slice';
import { useAppSelector } from '@shared/store/hooks';
import { appSelector } from '@shared/store/slices/app.slice';
import { memo, useContext, useEffect, useRef } from 'react';

/**
 * Indicator related constants.
 */
const MIN_PERCENTAGE = 0;
// const MAX_PERCENTAGE = 100;
const INDICATOR_UPDATE_INTERVAL = 15 * 1000;

/**
 * Returns the current time's position as a percentage (0–100) within the
 * visible EPG window, or null if "now" falls outside the window.
 */
const calculateProgress = (beginTime?: Date, endTime?: Date): number | null => {
  if (!beginTime || !endTime) return null;
  const now = new Date();
  if (now < beginTime) return null;
  return (
    ((now.getTime() - beginTime.getTime()) /
      (endTime.getTime() - beginTime.getTime())) *
    100
  );
};

/**
 * Vertical "current time" needle overlaid on the EPG timeline.
 *
 * Reads `beginTime`/`endTime` from the timeline config and writes the
 * computed percentage position back via `setIndicator` so sibling components
 * (e.g. EPG cards) can align to the same position without recalculating.
 * The position is refreshed every 5 seconds while mounted.
 *
 * Renders nothing when "now" is outside the visible EPG window.
 */
function EpgTimeIndicator() {
  const { status } = useAppSelector(appSelector);
  const { config } = useContext(MainContext);
  const layout = useAppSelector(selectLayout);
  const { start, end, indicator, setIndicator } = config.timeline || {};
  const indicatorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!setIndicator) return;
    setIndicator(calculateProgress(start, end));
    const interval = setInterval(() => {
      setIndicator && setIndicator(calculateProgress(start, end));
    }, INDICATOR_UPDATE_INTERVAL);
    // Clear shared indicator state on unmount to avoid stale position leaking
    // to other views that reuse the same context.
    return () => {
      setIndicator && setIndicator(null);
      clearInterval(interval);
    };
  }, [start, end, layout, status.visibility, setIndicator]);

  if (indicator == null || indicator <= MIN_PERCENTAGE) {
    return null;
  }
  return (
    <EpgTimeIndicatorWrapper indicator={indicator}>
      <EpgTimeIndicatorHeader />
      <EpgTimeIndicatorBody id='indicatorBody' ref={indicatorRef} />
    </EpgTimeIndicatorWrapper>
  );
}

export default memo(EpgTimeIndicator);
